// src/VentanillaElectronica.js — JCUMAS 060.013
// Puerta publica (sin login) para identificarse con certificado digital via
// AutoFirma: reto -> firma CAdES -> verificacion en backend -> si el NIF ya
// tiene usuario, entra igual que con Login.js; si es la primera vez, pide
// Email/Telefono antes de dar de alta (el certificado no trae un contacto
// fiable). Reutiliza el mismo patron de AutoScript que FirmaCertificado.js.
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import logo from "./Images/logo-jcumas.png";

const BASE_URL = process.env.REACT_APP_API_URL;

const VentanillaElectronica = () => {
  const navigate = useNavigate();
  const [paso, setPaso] = useState("inicio"); // inicio | identificando | completar_registro | error
  const [error, setError] = useState("");
  const [datosPendiente, setDatosPendiente] = useState(null); // {token, nif, nombre, apellidos}
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (window.AutoScript) {
      window.AutoScript.cargarAppAfirma();
    } else {
      console.error("AutoScript no se ha cargado correctamente.");
    }
  }, []);

  const entrarConToken = (data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("rol", data.rol);
    localStorage.setItem("id", data.id);
    localStorage.setItem("esSocio", data.esSocio);
    localStorage.setItem(
      "nombreUsuario",
      [data.nombre, data.apellidos].filter(Boolean).join(" ")
    );
    navigate("/home");
  };

  const identificarse = async () => {
    setError("");
    setPaso("identificando");
    try {
      const resReto = await fetch(`${BASE_URL}/auth/certificado/reto`);
      if (!resReto.ok) throw new Error("No se ha podido iniciar la identificación.");
      const { reto_id, nonce } = await resReto.json();

      window.AutoScript.sign(
        btoa(nonce),
        "SHA256withRSA",
        "CAdES",
        "",
        async (firmado) => {
          try {
            const resVerificar = await fetch(`${BASE_URL}/auth/certificado/verificar`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ reto_id, firma: firmado }),
            });
            const data = await resVerificar.json();

            if (!resVerificar.ok) {
              setError(data.error || "No se ha podido verificar el certificado.");
              setPaso("inicio");
              return;
            }

            if (data.pending_registration) {
              setDatosPendiente(data);
              setPaso("completar_registro");
            } else {
              entrarConToken(data);
            }
          } catch (e) {
            setError("No se pudo conectar con el servidor.");
            setPaso("inicio");
          }
        },
        (type, message) => {
          console.error("Error al firmar:", type, message);
          setError("No se ha podido firmar con el certificado: " + message);
          setPaso("inicio");
        }
      );
    } catch (e) {
      setError(e.message || "Error al iniciar la identificación.");
      setPaso("inicio");
    }
  };

  const completarRegistro = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      const res = await fetch(`${BASE_URL}/auth/certificado/completar-registro`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${datosPendiente.token}`,
        },
        body: JSON.stringify({ Email: email, Telefono: telefono }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "No se ha podido completar el registro.");
        setGuardando(false);
        return;
      }
      entrarConToken(data);
    } catch (e) {
      setError("No se pudo conectar con el servidor.");
      setGuardando(false);
    }
  };

  return (
    <Container
      fluid
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh", backgroundColor: "#eef4fb" }}
    >
      <Row className="w-100 justify-content-center">
        <Col xs={11} sm={10} md={8} lg={6} xl={5}>
          <Card
            className="border-0"
            style={{ borderRadius: "var(--sp-radius)", boxShadow: "0 8px 32px rgba(13,110,253,0.14)", overflow: "hidden" }}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderBottom: "1px solid var(--sp-border-strong)",
                padding: "28px 24px 22px",
                textAlign: "center",
              }}
            >
              <img src={logo} alt="Logo JCUMAS 060.013" className="img-fluid" style={{ maxWidth: "300px", height: "auto" }} />
              <p style={{ color: "#0d6efd", marginTop: "14px", marginBottom: 0, fontSize: "13px", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 600 }}>
                Ventanilla Electrónica
              </p>
            </div>

            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" className="mb-3 py-2 text-center small">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {error}
                </Alert>
              )}

              {paso !== "completar_registro" && (
                <>
                  <p className="text-center mb-4" style={{ color: "#555", fontSize: "14px" }}>
                    Identifícate con tu certificado digital para acceder a tus expedientes
                    y presentar solicitudes.
                  </p>
                  <Button
                    className="w-100"
                    disabled={paso === "identificando"}
                    onClick={identificarse}
                    style={{ padding: "12px", backgroundColor: "#0d6efd", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "15px" }}
                  >
                    {paso === "identificando" ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Esperando certificado…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-badge me-2"></i>
                        Identificarme con certificado
                      </>
                    )}
                  </Button>
                  <p className="text-center mt-3 mb-0" style={{ color: "#999", fontSize: "12px" }}>
                    ¿Ya tienes usuario y contraseña?{" "}
                    <a href="/login" style={{ color: "#0d6efd" }}>Accede aquí</a>
                  </p>
                </>
              )}

              {paso === "completar_registro" && datosPendiente && (
                <>
                  <p className="text-center mb-3" style={{ color: "#555", fontSize: "14px" }}>
                    Hola {datosPendiente.nombre || ""} {datosPendiente.apellidos || ""} (NIF {datosPendiente.nif}).
                    Es la primera vez que accedes así, déjanos un contacto para avisarte del estado de tus solicitudes.
                  </p>
                  <Form onSubmit={completarRegistro}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="tu@email.com"
                      />
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>Teléfono (opcional)</Form.Label>
                      <Form.Control
                        type="tel"
                        value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                        placeholder="600 000 000"
                      />
                    </Form.Group>
                    <Button type="submit" disabled={guardando} className="w-100" style={{ padding: "12px", backgroundColor: "#0d6efd", border: "none", borderRadius: "8px", fontWeight: 600 }}>
                      {guardando ? <Spinner animation="border" size="sm" /> : "Confirmar y entrar"}
                    </Button>
                  </Form>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VentanillaElectronica;
